import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useState } from "react";
import CardKit from "@/shared/components/kit/card";
import {
  numberSeparator,
  toEnglishNumber,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import CustomSelectNormal from "@/shared/components/common/select/selectNormal";
import { fetchListActivePazirandeBankAccounts } from "@/api/merchant/request.get";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { fetchListRegisterWithdrawRequest } from "@/api/merchant/request.post";
interface ContentProps {
  dataBalancePazirande: any;
  setSelected: any;
  selected: any;
  refetchDataList: any;
}
/**
 * Content
 *
 * کامپوننتی برای ثبت درخواست برداشت از کیف پول کاربران.
 *
 * ویژگی‌ها:
 * - امکان وارد کردن مبلغ مورد نظر جهت برداشت با محدودیت حداکثر 100,000,000 ریال
 * - انتخاب کارت بانکی مرتبط برای واریز وجه
 * - نمایش پیام راهنما و توضیحات جهت برداشت
 * - استفاده از react-query برای واکشی لیست کارت‌های فعال پذیرنده
 * - ثبت درخواست برداشت با استفاده از mutate و نمایش پیام موفقیت یا خطا با toast
 * - دکمه تایید همراه با CircularProgress در هنگام پردازش درخواست
 *
 * @param {Object} props
 * @param {any} props.dataBalancePazirande - موجودی کیف پول پذیرنده
 * @param {any} props.setSelected - تابع برای تعیین کارت انتخابی
 * @param {any} props.selected - کارت انتخاب شده
 * @param {any} props.refetchDataList - تابع برای بروزرسانی لیست تراکنش‌ها پس از ثبت درخواست
 *
 * @returns JSX.Element
 */

const Content = ({
  dataBalancePazirande,
  setSelected,
  selected,
  refetchDataList,
}: ContentProps) => {
  const [amountState, setAmountState] = useState("");

  const { data: dataListPazirandeClubs, refetch } = useQuery({
    queryKey: ["dataListPazirandeClubs-data"],
    queryFn: fetchListActivePazirandeBankAccounts,
  });

  const { mutate: submitRegisterWithdrawRequest, isPending: isPendingLogin } =
    useMutation({
      mutationFn: fetchListRegisterWithdrawRequest,
      onSuccess: (data: any) => {
        refetchDataList();
        toast.success("درخواست با موفقیت ارسال شد");
        refetch();
      },
      onError: (error: AxiosError<{ message: string }>) => {
        toast.error(error?.response?.data?.message || "خطا در ثبت اطلاعات");
      },
    });

  const handleClickRegister = () => {
    const requestBody = {
      price: amountState,
      pazirande_bank_id: selected,
    };
    submitRegisterWithdrawRequest(requestBody);
  };

  return (
    <CardKit
      sx={{
        bgcolor: "#434343",
        width: "100%",
        boxShadow: "none",
        height: "100%",
      }}
    >
      <Grid container alignItems={"start"} gap={3}>
        <StackKit alignItems={"start"}>
          <TypoGraphyKit variant="body1" sx={{ textAlign: "right" }}>
            در این بخش شما میتوانید درخواست برداشت از کیف پول خود را ثبت نمایید.
            مبلغ مورد نظر پس از بررسی و تایید به حساب انتخاب شده توسط شما واریز
            خواهدشد.
          </TypoGraphyKit>
        </StackKit>
        <Grid
          size={12}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
          }}
        >
          <TypoGraphyKit
            variant="body1"
            sx={{ textAlign: "right", width: "100%" }}
          >
            مبلغ مورد نظر جهت برداشت از کیف پول را وارد نمایید.
          </TypoGraphyKit>
          <TextField
            className="w-full"
            placeholder={`${numberSeparator(200000)}`}
            value={toPersianNumber(numberSeparator(amountState))}
            onChange={(e) => {
              const englishNumber = toEnglishNumber(e.target.value);
              const rawValue = englishNumber.replace(/[^0-9]/g, "");
              if (rawValue === "" || Number(rawValue) <= 10000000000) {
                setAmountState(rawValue);
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">ریال</InputAdornment>
              ),
            }}
            sx={{ background: "#CFCFCF" }}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <StackKit
            direction={{ xs: "column", md: "row" }}
            sx={{ width: "100%" }}
            justifyContent={"space-between"}
          >
            <BoxKit sx={{ width: "100%", textAlign: "right" }}>
              <TypoGraphyKit variant="body1">
                کارت مورد نظر جهت برداشت وجه را انتخاب نمایید.
              </TypoGraphyKit>
            </BoxKit>
            <CustomSelectNormal
              label=""
              value={selected}
              onChange={(e) => {
                const selectedId = Number(e.target.value);
                setSelected(`${selectedId}`);
              }}
              sx={{
                "& .MuiSelect-select": {
                  backgroundColor: "#89C6CE !important",
                },
              }}
              options={dataListPazirandeClubs?.data ?? []}
              getOptionLabel={(item) => `${item.card}`}
              getOptionValue={(item) => item.id}
              width={"100%"}
            />
          </StackKit>
        </Grid>
        <Grid
          size={12}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <TypoGraphyKit variant="body1" sx={{ color: "#666666" }}>
            سقف برداشت روزانه: {numberSeparator(100000000)} ریال{" "}
          </TypoGraphyKit>
          <CustomButton
            onClick={handleClickRegister}
            sx={{ minWidth: "100px" }}
          >
            {isPendingLogin ? (
              <CircularProgress sx={{ color: "#fff" }} size={24} />
            ) : (
              <TypoGraphyKit>تایید و ادامه</TypoGraphyKit>
            )}
          </CustomButton>
        </Grid>
      </Grid>
    </CardKit>
  );
};

export default Content;
